import { useCallback, useEffect, useMemo, useState, type ReactNode } from "react";
import { useLocation } from "react-router";
import type { DrillTarget } from "../../lib/drill/targets";
import { DrillContext, keyOf, type OpenPanel } from "./context";

/**
 * The open drill panels. Alone in its own file: `context.ts` beside it holds the
 * context, `keyOf` and `useDrill`, because a module that exports a component and
 * a hook breaks Fast Refresh for every consumer of either.
 */
export function DrillProvider({ children }: { children: ReactNode }) {
  const [panels, setPanels] = useState<readonly OpenPanel[]>([]);
  const { pathname } = useLocation();

  useEffect(() => {
    // Cleared on a route change: a panel is a read about a row on the register
    // the operator has just left, and carrying it forward is a window over a
    // page it does not belong to (§7.8). The router is the external system this
    // effect synchronises with.
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setPanels([]);
  }, [pathname]);

  const open = useCallback((target: DrillTarget) => {
    setPanels((current) => {
      const key = keyOf(target);
      // Keyed by kind-and-id: opening the same record twice re-focuses the panel
      // that is already up rather than stacking a duplicate of it.
      if (current.some((panel) => panel.key === key)) return current;
      return [...current, { ...target, key }];
    });
  }, []);

  const close = useCallback((key: string) => {
    setPanels((current) => current.filter((panel) => panel.key !== key));
  }, []);

  const value = useMemo(() => ({ panels, open, close }), [panels, open, close]);
  return <DrillContext value={value}>{children}</DrillContext>;
}
